import { useCallback, useMemo, useState } from 'react';
import request from 'lib/request';
import {
  useServerExport,
  type ServerExportStatus,
} from 'lib/hooks/useServerExport';
import {
  type WellSelectionState,
  buildWellSelectionQueryFilter,
  countSelectedWells,
} from './atoms';
import { fetchAllFilteredUwis, type WorkspaceWellsFilter } from './hooks';

type StartExportResponse = { exportId: string };

type Options = {
  workspaceId: number;
  dcaScenarioId?: number | null;
  wellsMap: WellSelectionState;
  totalWells: number;
  /**
   * What the wells table is filtered to. Select-all is stored as "everything
   * except these", so without the filter the export would resolve every well in
   * the workspace rather than the ones the user can see.
   */
  filters: WorkspaceWellsFilter;
};

/** The controls and status `useWorkspaceExport` returns. */
interface WorkspaceExport {
  /** Starts the export for the wells the user has selected. */
  startExport: () => Promise<void>;
  /** Where the export has got to, and its result once it finishes. */
  exportStatus: ServerExportStatus;
  /** True while an export is running. */
  isExporting: boolean;
  /** Clears the status back to idle. */
  resetExport: () => void;
  /** How many wells are selected. */
  selectedCount: number;
  /** How many wells that were selected when the running export started. */
  exportedWellCount: number;
}

/**
 * Runs the entire workspace export process: resolves the selected wells, sends
 * them to the async export endpoint, and downloads the workbook when received.
 *
 * @param {Options} options - Workspace, scenario, and the current selection.
 * @return {WorkspaceExport} Controls and status for the export.
 */
export const useWorkspaceExport = ({
  workspaceId,
  dcaScenarioId,
  wellsMap,
  totalWells,
  filters,
}: Options): WorkspaceExport => {
  const selectedCount = useMemo(
    () => countSelectedWells(wellsMap, totalWells),
    [wellsMap, totalWells]
  );

  const [exportedWellCount, setExportedWellCount] = useState(0);

  const exportFn = useCallback(async (): Promise<StartExportResponse> => {
    const selection = buildWellSelectionQueryFilter(wellsMap);
    const uwis =
      selection.uwis ??
      (await fetchAllFilteredUwis({
        workspaceId,
        dcaScenarioId,
        ...filters,
        excludeUwis: selection.excludeUwis,
      }));

    // Counted here rather than from the selection, which is workspace-wide and
    // would overstate an export the table's filter has narrowed.
    setExportedWellCount(uwis.length);

    const response = await request.post<
      StartExportResponse,
      { error?: string; errors?: Array<{ message?: string }> }
    >(
      `/dca_workspaces/${workspaceId}/exports`,
      { export: { dcaScenarioId, uwis } },
      new AbortController()
    );

    if (response.type !== 'success') {
      const body = response.type === 'error' ? response.data : undefined;
      throw new Error(
        body?.error ??
          body?.errors?.[0]?.message ??
          'Could not start the export'
      );
    }

    return { exportId: response.data.exportId };
  }, [workspaceId, dcaScenarioId, wellsMap, filters]);

  const {
    startExport: start,
    exportStatus,
    isExporting,
    resetExport,
  } = useServerExport({
    channel: `dca_workspace_${workspaceId}`,
    event: 'dca_workspace_export_complete',
    exportFn,
  });

  return {
    startExport: start,
    exportStatus,
    isExporting,
    resetExport,
    selectedCount,
    exportedWellCount,
  };
};
